import { ArrowRight, CheckCircle2, Gift, MessageSquareText, PhoneCall } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { hongKongContactPath, hongKongDownloadPath } from "../../routes";
import { reportLeadEntryConversion } from "../../tracking";
import { DownloadLink } from "../DownloadLink";

/* 香港站收費方案：結構與樣式對齊牙科方案頁共用的 Pricing 元件
   （src/clawease/components/Pricing.tsx，見 /ai-receptionist/solutions/dental-clinics），
   文案改為繁體中文。三張方案卡 + 月付/年付切換，Pro 標示「最受歡迎」。 */
type BillingCycle = "monthly" | "annual";
type PlanKey = "free" | "pro" | "team";

type PlanFeature = { title: string; detail?: string; note?: string; highlight?: string };
type Plan = { name: string; summary: string; note?: string; cta: string; popular?: boolean; features: PlanFeature[] };

const planKeys: PlanKey[] = ["free", "pro", "team"];

const plans: Record<PlanKey, Plan> = {
  free: {
    name: "Free",
    summary: "支援 DIY，自行配置及實現個人工作流。",
    note: "無需訂閱，立即體驗 ClawEase。",
    cta: "免費開始",
    features: [
      {
        title: "首次註冊送 US$20 永久額度",
        note: "≈ 5,000 條訊息／200 分鐘語音",
        highlight: "永久有效・可抵扣任意 AI 費用",
      },
      { title: "每月贈送 US$5 月額度", detail: "同樣可抵扣任意 AI 費用。" },
      { title: "DIY 自助工作流", detail: "自行配置接待流程。" },
      { title: "電郵、電話與網頁預約", detail: "多渠道自動接待。" },
      { title: "廣東話、普通話及英語", detail: "結合知識庫，按你的資料自然回答。" },
    ],
  },
  pro: {
    name: "Pro",
    summary: "專業團隊對接交付，按業務需求完成配置。",
    cta: "聯絡我們",
    popular: true,
    features: [
      { title: "每月包含 US$49 使用額度", detail: "覆蓋日常接待。" },
      { title: "包含 Free 全部功能", detail: "完整線上自動化。" },
      { title: "支援 25 種語言", detail: "服務多語客戶。" },
      { title: "WhatsApp 等更多渠道", detail: "連接更多入口。" },
      { title: "自動跟進", detail: "推進確認與轉化。" },
    ],
  },
  team: {
    name: "Team",
    summary: "為多人團隊提供共享接待與業務協作。",
    cta: "聯絡我們",
    features: [
      { title: "每月包含 US$119 使用額度", detail: "團隊共享額度。" },
      { title: "包含 Pro 全部功能", detail: "多渠道與自動跟進。" },
      { title: "支援 3 個團隊帳號", detail: "多人共享協作。" },
      { title: "專業 PMS 與預約系統", detail: "專業系統對接。" },
      { title: "智能分析與團隊通知", detail: "同步結果與摘要。" },
    ],
  },
};

const prices: Record<BillingCycle, Record<PlanKey, number>> = {
  monthly: { free: 0, pro: 49, team: 99 },
  annual: { free: 0, pro: 39, team: 79 },
};

function BillingToggle({ billing, onChange }: { billing: BillingCycle; onChange: (billing: BillingCycle) => void }) {
  const labels: Record<BillingCycle, string> = { monthly: "月付", annual: "年付" };
  return (
    <div className="inline-flex rounded-full bg-white p-1 shadow-[0_12px_28px_rgba(42,19,41,.07)]">
      {(["monthly", "annual"] as BillingCycle[]).map((item) => {
        const active = billing === item;
        return (
          <button
            key={item}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(item)}
            className={`inline-flex h-9 items-center justify-center gap-2 rounded-full px-5 text-[13px] font-extrabold transition-colors ${
              active ? "bg-[#17141c] text-white" : "text-[#5f5565] hover:text-[#100f12]"
            }`}
          >
            {labels[item]}
            {item === "annual" ? (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] ${active ? "bg-white text-[#5223b1]" : "bg-[#f3eefa] text-[#5223b1]"}`}>
                慳 20%
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function PlanIcon({ plan }: { plan: PlanKey }) {
  const Icon = plan === "free" ? Gift : plan === "pro" ? MessageSquareText : PhoneCall;
  return (
    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#f3eefa] text-[#5b24c9]">
      <Icon size={22} strokeWidth={2.5} />
    </span>
  );
}

function FeatureItem({ feature }: { feature: PlanFeature }) {
  return (
    <li className="flex gap-2.5 text-[#5f5565]">
      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#5b24c9]" strokeWidth={2.6} />
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-extrabold leading-[1.25] text-[#4d4652]">
          <span>{feature.title}</span>
          {feature.note && (
            <span className="shrink-0 rounded-md bg-[#f3eefa] px-1.5 py-0.5 text-[11px] font-bold text-[#5223b1]">{feature.note}</span>
          )}
        </span>
        {feature.detail ? <span className="mt-0.5 block text-[11px] font-semibold leading-[1.45] text-[#8a7f8f]">{feature.detail}</span> : null}
        {feature.highlight ? <span className="mt-1 block text-[11px] font-semibold leading-[1.45] text-[#8a7f8f]">{feature.highlight}</span> : null}
      </span>
    </li>
  );
}

function PlanCard({ plan, billing }: { plan: PlanKey; billing: BillingCycle }) {
  const data = plans[plan];
  const { search } = useLocation();
  const note = plan === "free" ? data.note : billing === "annual" ? "按年支付，月費 8 折。" : "按月支付，可隨時取消。";

  return (
    <article
      className={`relative flex h-full flex-col rounded-[20px] bg-white p-5 sm:p-6 ${
        data.popular
          ? "shadow-[0_26px_68px_rgba(82,35,177,.14)]"
          : "shadow-[0_22px_58px_rgba(34,19,48,.07)]"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <PlanIcon plan={plan} />
            <div>
              <h3 className="text-[27px] font-extrabold leading-none tracking-[-0.03em] text-[#17141c] sm:text-[30px]">{data.name}</h3>
            </div>
          </div>
        </div>
        {data.popular ? (
          <div className="inline-flex h-8 flex-shrink-0 items-center justify-center rounded-full bg-[#5b24c9] px-4 text-[12px] font-black text-white shadow-[0_12px_30px_rgba(91,36,201,.18)]">
            最受歡迎
          </div>
        ) : null}
      </div>

      <p className="mt-4 max-w-[500px] text-[14px] font-semibold leading-6 tracking-[-0.01em] text-[#4d4652] lg:min-h-[48px]">{data.summary}</p>

      <div className="mt-3 flex items-end gap-2">
        <span className="text-[46px] font-black leading-none tracking-[-0.06em] text-[#17141c] sm:text-[50px]">${prices[billing][plan]}</span>
        <span className="pb-1.5 text-[14px] font-extrabold text-[#7b7380]">/月</span>
      </div>
      <p className="mt-1.5 text-[12px] font-bold leading-5 text-[#8a7f8f]">{note}</p>

      <div className="mt-4 h-px w-full bg-[#eee4ef]" />

      <ul className="mt-4 grid gap-x-4 gap-y-3 lg:gap-y-[10px]">
        {data.features.map((feature) => (
          <FeatureItem key={feature.title} feature={feature} />
        ))}
      </ul>

      <div className="mt-auto pt-5">
        {plan === "free" ? (
          <DownloadLink
            to={hongKongDownloadPath(`hk_pricing_${plan}_${billing}`, plan, search)}
            onClick={() => reportLeadEntryConversion(search)}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#e3d6e6] bg-white px-6 text-[14px] font-extrabold leading-none text-[#17141c] no-underline transition-transform hover:-translate-y-0.5 hover:bg-[#fbf8fc]"
          >
            <span className="leading-none">{data.cta}</span>
            <ArrowRight className="h-4 w-4 flex-shrink-0 translate-y-px" strokeWidth={2.7} />
          </DownloadLink>
        ) : (
          <Link
            to={hongKongContactPath(undefined, `hk_pricing_${plan}_${billing}_contact`, search)}
            onClick={() => reportLeadEntryConversion(search)}
            className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-full px-6 text-[14px] font-extrabold leading-none no-underline transition-transform hover:-translate-y-0.5 ${
              data.popular
                ? "bg-[#5b24c9] text-white shadow-[0_18px_46px_rgba(91,36,201,.24)]"
                : "border border-[#e3d6e6] bg-white text-[#17141c] hover:bg-[#fbf8fc]"
            }`}
          >
            <span className="leading-none">{data.cta}</span>
            <ArrowRight className="h-4 w-4 flex-shrink-0 translate-y-px" strokeWidth={2.7} />
          </Link>
        )}
      </div>
    </article>
  );
}

export function HomePricingHK() {
  const [billing, setBilling] = useState<BillingCycle>("annual");

  return (
    <>
      <div className="ce-hk-section-inner ce-hk-how-head">
        <h2>先免費開始，<br />按業務需要再升級。</h2>
        <p className="ce-hk-section-lede">Free 方案無需訂閱費；有更多渠道、語言或團隊協作需要時，可選擇 Pro 或 Team。</p>
        <div className="mt-5">
          <BillingToggle billing={billing} onChange={setBilling} />
        </div>
      </div>
      <div className="ce-hk-section-inner ce-hk-pricing-cards">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 lg:items-stretch">
          {planKeys.map((plan) => (
            <PlanCard key={plan} plan={plan} billing={billing} />
          ))}
        </div>
      </div>
    </>
  );
}
